import React, { useContext, useRef, useEffect, useState } from 'react';
import { intlFormat } from 'date-fns';

import { styled } from '@material-ui/core/styles';

import { Message, MessageInput } from './social/index';
import { useDataHelper } from 'utils/dataHelper';
import { SdmSpacer, SdmTypography, SdmScrollable } from 'components';
import { AuthContext } from 'context/AuthContextProvider';
import { SizeContext } from 'context/SizeContextProvider';

import { theme } from 'theme';
import { isIOS } from 'react-device-detect';

const MessageInputContainer = styled('div')(({ ismobile }) => ({
  paddingRight: ismobile ? 8 : 30,
  paddingLeft: ismobile ? 8 : 30,
  ...(ismobile &&
    isIOS && {
      position: 'fixed',
      bottom: 0,
      right: 0,
      left: 0,
    }),
}));

const Filler = styled('div')({
  flexGrow: 1,
});

const isElementInViewport = (el) => {
  var rect = el.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const Social = ({
  i18n,
  messages = [],
  publish,
  hasBeenBanned,
  hasChatExpired,
  isModerator,
  lang,
  handleDelete,
  fetchOlderMessages,
  isXsDown,
  isVip,
  isMenuOpen,
}) => {
  const { user } = useContext(AuthContext);
  const { getInnerHeight } = useContext(SizeContext);
  const chatContainerRef = useRef(null);
  const hiddenDivTop = useRef();
  const hiddenDivBottom = useRef();

  const dataHelper = useDataHelper();

  const [isFirstTime, setIsFirstTime] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    // auto scroll chat
    if (messages.length > 0 && isFirstTime) {
      setIsFirstTime(false);
      setTimeout(() => {
        scrollChatToBottom();
      });
    } else if (hiddenDivTop.current && hiddenDivBottom.current) {
      const isHiddenDivTopInViewport = isElementInViewport(
        hiddenDivTop.current
      );
      const isHiddenDivBottomInViewport = isElementInViewport(
        hiddenDivBottom.current
      );
      (isHiddenDivTopInViewport || isHiddenDivBottomInViewport) &&
        scrollChatToBottom();
    }
  }, [messages]);

  // Functions
  const scrollChatToBottom = () => {
    chatContainerRef?.current?.scrollToBottom();
  };

  const convertInternationDate = (date) => {
    return intlFormat(
      new Date(date),
      {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      {
        locale: dataHelper.getLangCode(lang),
      }
    );
  };

  const renderMessage = (m) => {
    // eslint-disable-next-line
    let userInfo;
    let nickName;
    switch (m?.message?.content?.type) {
      /* case 'sys':
        return (
          <ServiceMessage
            message={`${m?.message?.content?.userInfo?.user?.givenName} ${
              m?.message?.content?.userInfo?.user?.familyName
            } ${i18n.serviceMessages[m?.message?.content?.text]?.label}`}
            onBan={() => handleBan(m?.message?.content?.userInfo?.id)}
            onUnban={() => handleUnban(m?.message?.content?.userInfo?.id)}
          />
        ); */
      case 'timeSeparator':
        return (
          <>
            <SdmSpacer height={5} />
            <SdmTypography
              variant={'h2Regular'}
              style={{ color: 'black', fontFamily: 'Suisse-Intl' }}
              textAlign={'center'}
            >
              {convertInternationDate(m.message.content.date)}
            </SdmTypography>
            <SdmSpacer height={5} />
          </>
        );
      case 'chat':
        userInfo = m.message.content.userInfo.user;
        nickName = `${
          userInfo.name
            ? userInfo.name
            : `${userInfo.givenName || ''} ${userInfo.familyName || ''}`
        }`;
        // const nickName = userInfo.role === userModel.enums.roles.USER ? `${userInfo.giv}`
        return (
          <Message
            message={m.message.content.text}
            nickName={nickName}
            avatar={m.message.content.userInfo?.user?.image}
            isMine={user?.id === m.message.content.userInfo?.id}
            isModerator={isModerator}
            isModerated={m.isModerated}
            youTranslation={i18n.messages.youTranslation}
            i18n={i18n}
            isVip={userInfo?.isVip}
            onDelete={(msg) => handleDelete(m.timetoken, msg)}
            messageTime={m?.timetoken}
            isMobile={isXsDown}
          />
        );
      default:
        break;
    }
  };

  return (
    <div
      style={{
        // display: 'flex',
        // flexDirection: 'column',
        height: `calc(${getInnerHeight()}px  - ${isXsDown ? 139 : 180}px)`,
        marginTop: 0,
      }}
    >
      <SdmScrollable
        ref={chatContainerRef}
        loadMoreContentCallback={fetchOlderMessages}
        setHasScrolled={setHasScrolled}
        hideBtn={isMenuOpen}
        style={{
          padding: `0 ${isXsDown ? 8 : 30}px`,
          height: `calc(100vh - ${isIOS && isXsDown ? 210 : 180}px)`,
          width: '100%',
          position: 'relative',
        }}
      >
        {messages.map((m, idx) => {
          return (
            <React.Fragment key={m?.message?.id || idx}>
              {idx === messages.length - 1 && <div ref={hiddenDivTop} />}
              {renderMessage(m, idx)}
              {idx === messages.length - 1 && <div ref={hiddenDivBottom} />}
            </React.Fragment>
          );
        })}
      </SdmScrollable>
      <Filler />
      <MessageInputContainer ismobile={isXsDown ? 1 : 0}>
        {hasBeenBanned ? (
          <SdmTypography
            variant={'h4'}
            textAlign={'center'}
            style={{ color: theme.colors.primary.red }}
          >
            {i18n.bannedError}
          </SdmTypography>
        ) : hasChatExpired ? (
          <SdmTypography
            variant={'h4'}
            textAlign={'center'}
            style={{ color: theme.colors.primary.red }}
          >
            {i18n.expiredError}
          </SdmTypography>
        ) : (
          <MessageInput
            i18n={i18n.messageInput}
            publish={publish}
            scrollChatToBottom={scrollChatToBottom}
            isXsDown={isXsDown}
            isVip={isVip}
            hasScrolled={hasScrolled}
            isMenuOpen={isMenuOpen}
          />
        )}
      </MessageInputContainer>
    </div>
  );
};

export { Social };
